import {
  ChangeDetectorRef,
  Component,
  inject,
  OnInit,
  OnDestroy,
} from '@angular/core';
import { SvgIconComponent } from 'angular-svg-icon';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';
import { NzDropDownModule } from 'ng-zorro-antd/dropdown';
import { NzSwitchModule } from 'ng-zorro-antd/switch';
import { NzTableModule, NzTableQueryParams } from 'ng-zorro-antd/table';
import { DrawerService } from '../../../../core/services/drawer.service';
import { finalize, Subject, take, takeUntil } from 'rxjs';
import { OrgManagementMode } from '../../manage-organization.enums';
import { ManageOrganizationDrawerParams } from '../../manage-organization.interface';
import { SubscriptionService } from '../subscription.service';
import { OrganizationService } from '../../../organizations/organization.service';
import { CommonModule } from '@angular/common';
import {
  Subscription,
  SubscriptionApiResponsePaginated,
} from '../subscription.inteface';
import {
  FormArray,
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { DRAWER_IDS } from '../../../../core/constants/drawer-ids.constant';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { NzInputModule } from 'ng-zorro-antd/input';
import { ConfirmDialogComponent } from '../../../../components/confirm-dialog/confirm-dialog.component';
import { ErrorResponse } from '../../../../core/models/error.models';
import { CommonService } from '../../../../shared/services/common.service';
import { AuditContextService } from '../../../audit-logs/audit-context.service';
import { TimeZoneService } from '../../../../core/services/timezone.service';

@Component({
  selector: 'app-subscription-listing',
  standalone: true,
  imports: [
    NzTableModule,
    // NzPaginationModule,
    NzDatePickerModule,
    NzButtonModule,
    SvgIconComponent,
    NzDropDownModule,
    NzSwitchModule,
    CommonModule,
    FormsModule,
    NzModalModule,
    NzInputModule,
    ReactiveFormsModule,
    ConfirmDialogComponent,
  ],
  templateUrl: './subscription-listing.component.html',
  styleUrl: './subscription-listing.component.scss',
})
export class SubscriptionListingComponent implements OnInit, OnDestroy {
  listOfColumn = [
    {
      title: 'CMS',
      compare: null,
    },
    {
      title: 'E-Learning',
      compare: null,
    },
    {
      title: 'Start Date',
      key: 'startDate',
      compare: null,
      width: 300,
    },
    {
      title: 'End Date',
      key: 'endDate',
      compare: null,
    },
    {
      title: 'Remarks',
      compare: null,
    },
    {
      title: '',
      compare: null,
    },
  ];
  constructor(
    private readonly drawerService: DrawerService,
    private readonly cdr: ChangeDetectorRef,
    private readonly fb: FormBuilder,
    private readonly commonService: CommonService,
    private readonly orgService: OrganizationService,
    private readonly auditContextService: AuditContextService,
    private readonly timeZoneService: TimeZoneService
  ) {
    this.subscriptionForm = this.fb.group({
      subscriptions: this.fb.array([]),
    });
    this.setupDrawerParams();
  }

  subscriptionForm!: FormGroup;
  editingIndex: number | null = null;
  loading = false;
  isDeleting = false;
  errorMessage = '';
  isVisible = false;
  pageSize = 10;
  pageIndex = 1;
  totalRow = 0;
  sort = '';
  sortOrder: number | undefined = undefined;
  deleteId = 0;
  originalData: Subscription = {
    id: 0,
    startDate: '',
    endDate: '',
    isCMS: false,
    isLearning: false,
    remarks: '',
    organizationId: 0,
  };
  isUpdating: Record<string, boolean> = {};
  readonly subscriptionMode = OrgManagementMode;
  private readonly destroy$ = new Subject<void>();
  private readonly subscriptionService = inject(SubscriptionService);
  orgParams: ManageOrganizationDrawerParams = {
    organizationId: 0,
    mode: OrgManagementMode.VIEW,
  };
  subscriptions$ = this.subscriptionService.subscription$;

  ngOnInit(): void {
    this.subscriptionForm = this.fb.group({
      subscriptions: this.fb.array([
        this.fb.group({
          id: [null], // you can modify the default values as per your requirement
          startDate: [new Date()],
          endDate: [new Date()],
          isCMS: [false],
          isLearning: [false],
          remarks: [
            { value: '', disabled: this.isViewMode },
            Validators.maxLength(500),
          ],
        }),
      ]),
    });
    this.subscriptions$.subscribe(
      (data: SubscriptionApiResponsePaginated | null) => {
        if (data && data?.result.items.length > 0) {
          this.createFormArray(data?.result.items);
        }
      }
    );
  }

  get isEditMode(): boolean {
    return this.orgParams.mode === this.subscriptionMode.EDIT;
  }
  get isViewMode(): boolean {
    return this.orgParams.mode === OrgManagementMode.VIEW;
  }
  get subscriptions(): FormArray {
    return this.subscriptionForm.get('subscriptions') as FormArray;
  }

  disableControl(index: number) {
    return this.editingIndex !== index;
  }

  onQueryParamsChange(params: NzTableQueryParams): void {
    this.pageSize = params.pageSize;
    this.pageIndex = params.pageIndex;
    const currentSort = params.sort.find(item => item.value !== null);
    this.sort = currentSort?.key || '';
    const sortOrderValue = currentSort?.value === 'ascend' ? 0 : 1;
    this.sortOrder = currentSort?.value ? sortOrderValue : undefined;
    this.loadData();
  }
  // Populate form dynamically
  createFormArray(subscriptions: Subscription[]): void {
    const formGroups = subscriptions.map(sub =>
      this.fb.group({
        id: [sub.id],
        startDate: [this.convertDateStringToDateObject(sub.startDate)],
        endDate: [this.convertDateStringToDateObject(sub.endDate)],
        isCMS: [sub.isCMS],
        isLearning: [sub.isLearning],
        remarks: [
          { value: sub.remarks, disabled: this.isViewMode },
          Validators.maxLength(500),
        ],
        organizationId: [sub.organizationId],
      })
    );
    this.subscriptions.clear();
    formGroups.forEach(group => this.subscriptions.push(group));
  }

  // Create a FormGroup for each subscription
  addNewRow(): void {
    const subscriptions = this.subscriptionForm.get(
      'subscriptions'
    ) as FormArray;
    const newRow = this.fb.group({
      id: [null], // you can modify the default values as per your requirement
      startDate: [new Date()],
      endDate: [new Date()],
      isCMS: [false],
      isLearning: [false],
      remarks: [
        { value: '', disabled: this.isViewMode },
        Validators.maxLength(500),
      ],
    });

    // Add the new row at the first position
    subscriptions.insert(0, newRow);
  }

  removeRow(index: number): void {
    const subscriptions = this.subscriptionForm.get(
      'subscriptions'
    ) as FormArray;

    const subscriptionFormGroup = this.subscriptions.at(index);
    subscriptionFormGroup.get('remarks')?.disable();
    // Remove the row at the specified index
    subscriptions.removeAt(index);
  }

  private convertDateStringToDateObject(
    dateString: string | Date | null
  ): Date | null {
    if (!dateString) {
      return null;
    }
    if (dateString instanceof Date) {
      return dateString;
    }
    // Use TimeZoneService to properly convert ISO date strings to Date objects
    // This ensures consistent parsing across all components
    return this.timeZoneService.convertToUserTimeZone(dateString);
  }

  isDate(value: Date | string): value is Date {
    return value instanceof Date;
  }

  disableDates(current: Date): boolean {
    // Disable dates before today
    const today = new Date();
    // Clear the time part to compare only the dates
    today.setHours(0, 0, 0, 0);
    return current && current < today;
  }

  disableEndBeforeStart(i: number): (current: Date) => boolean {
    return (current: Date): boolean => {
      const startDate = new Date(
        this.subscriptions.at(i).get('startDate')?.value
      );
      if (!startDate) {
        return false; // Allow all dates if no start date is selected
      }
      return current && current < new Date(startDate);
    };
  }

  private setupDrawerParams(): void {
    this.drawerService
      .getParams<ManageOrganizationDrawerParams>(DRAWER_IDS.MANAGE_ORG)
      .pipe(takeUntil(this.destroy$))
      .subscribe(params => {
        if (params) {
          this.orgParams = params;
        }
      });
  }
  addSubscription() {
    if (this.orgParams.mode !== this.subscriptionMode.CREATE) {
      this.addNewRow();
    }
    this.editingIndex = 0;
    this.orgParams.mode = this.subscriptionMode.CREATE;
    this.isUpdating[0] = true;
    const subscriptionFormGroup = this.subscriptions.at(0);
    subscriptionFormGroup.get('remarks')?.enable();
    this.toggleDisable();
  }
  editSub(id: number) {
    const subscriptionFirstFormGroup = this.subscriptions.at(0);
    if (subscriptionFirstFormGroup.value.id === null) {
      this.removeRow(0);
      id = id - 1;
    }
    this.orgParams.mode = OrgManagementMode.EDIT;
    this.editingIndex = id;
    const subscriptionFormGroup = this.subscriptions.at(id);
    subscriptionFormGroup.get('remarks')?.enable();
    // Get the value of the entire form group (returns an object of all controls)
    const subscriptionValue = subscriptionFormGroup.value;
    this.originalData = subscriptionValue;
    this.isUpdating[id] = true;
    this.toggleDisable();
  }
  loadData(): void {
    this.loading = true;
    this.subscriptionService
      .getSubscriptions({
        pageIndex: this.pageIndex,
        pageSize: this.pageSize,
        sortOrder: this.sortOrder,
        sort: this.sort,
        organizationId: Number(this.orgParams.organizationId),
      })
      .pipe(
        take(1), // Take only one response
        finalize(() => (this.loading = false)), // End loading after response
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: response => {
          this.totalRow = response.result.totalRows;
          this.orgParams.mode = OrgManagementMode.VIEW;
          this.cdr.detectChanges();

          this.loading = false;
          if (response?.result.items) {
            this.createFormArray(response.result.items);
          }
          this.cdr.detectChanges();
        },
        error: (error: ErrorResponse) => {
          this.errorMessage = error.message;
          setTimeout(() => (this.errorMessage = ''), 3000);
        },
      });
  }

  onCancel(index: number) {
    this.isUpdating[index] = false;
    if (this.orgParams.mode === OrgManagementMode.CREATE) {
      this.removeRow(index);
    }
    if (this.originalData && this.orgParams.mode !== OrgManagementMode.CREATE) {
      this.subscriptions.at(index).patchValue(this.originalData);
    }
    this.editingIndex = -1;
    this.toggleDisable();
    this.orgParams.mode = OrgManagementMode.VIEW;
  }

  submitScription(index: number) {
    const subscriptions = this.subscriptionForm.get(
      'subscriptions'
    ) as FormArray;
    const row = subscriptions.at(index); // Get the FormGroup at the given index
    const data = row.value;
    const payload: Subscription = {
      id: data.id || 0,
      isCMS: data.isCMS,
      isLearning: data.isLearning,
      organizationId: Number(this.orgParams.organizationId),
      remarks: data.remarks,
      startDate: this.isDate(data.startDate)
        ? data.startDate.toISOString()
        : data.startDate,
      endDate: this.isDate(data.endDate)
        ? data.endDate.toISOString()
        : data.endDate,
    };

    const _modifiedFields = this.buildSubscriptionModifiedFields(payload);
    const apiPayload = { ...payload, _modifiedFields };

    this.loading = true;
    const operation$ = this.isEditMode
      ? this.subscriptionService.updateSubScription(apiPayload)
      : this.subscriptionService.createSubscription(apiPayload);

    operation$
      .pipe(
        take(1),
        takeUntil(this.destroy$),
        finalize(() => {
          this.loading = false;
          if (this.isEditMode) this.isUpdating[index] = false;
        })
      )
      .subscribe({
        next: () => {
          this.commonService.notify(
            'success',
            `Subscription ${this.isEditMode ? 'updated' : 'created'} successfully`
          );
          // Reload data and refresh the form
          this.loadData();
          this.orgService.refreshOrganizations();
          this.isUpdating[index] = false;
          this.orgParams.mode = this.subscriptionMode.VIEW;
          this.editingIndex = -1;
          this.cdr.detectChanges();
        },
        error: error => {
          this.loading = false;
          this.handleError(error);
        },
      });
  }

  private buildSubscriptionModifiedFields(
    payload: Partial<Subscription>
  ): unknown[] {
    const { original, updated } = this.getSubscriptionAuditContext(payload);
    const { enrichedOriginal, enrichedUpdated } =
      this.getEnrichedSubscriptionAuditModels(original, updated);

    return this.auditContextService.buildFieldChanges(
      enrichedOriginal,
      enrichedUpdated,
      {
        sensitiveFields: [],
        labelMap: this.getSubscriptionAuditLabelMap(),
        excludeFields: this.getSubscriptionAuditExcludeFields(),
      }
    );
  }

  private getSubscriptionAuditContext(payload: Partial<Subscription>): {
    original: Partial<Subscription>;
    updated: Partial<Subscription>;
  } {
    if (!this.isEditMode) {
      return { original: {}, updated: payload };
    }

    return {
      original: { ...this.originalData },
      updated: { ...payload },
    };
  }

  private getSubscriptionAuditExcludeFields(): string[] {
    return ['id'];
  }

  private getSubscriptionAuditLabelMap(): Record<string, string> {
    return {
      isCMS: 'CMS',
      isLearning: 'E-Learning',
      startDate: 'Start Date',
      endDate: 'End Date',
      remarks: 'Remarks',
      organizationId: 'Organization',
    };
  }

  private getEnrichedSubscriptionAuditModels(
    original: Partial<Subscription>,
    updated: Partial<Subscription>
  ): {
    enrichedOriginal: Record<string, unknown>;
    enrichedUpdated: Record<string, unknown>;
  } {
    const organizationName = this.orgParams.organizationName || '';
    const enrichedOriginal: Record<string, unknown> = { ...original };
    const enrichedUpdated: Record<string, unknown> = { ...updated };

    if (enrichedOriginal['organizationId'] !== undefined) {
      enrichedOriginal['organizationId'] = organizationName;
    }

    if (enrichedUpdated['organizationId'] !== undefined) {
      enrichedUpdated['organizationId'] = organizationName;
    }

    if (enrichedOriginal['startDate'] !== undefined) {
      enrichedOriginal['startDate'] = this.formatDateAsYYYY_MM_DD(
        enrichedOriginal['startDate']
      );
    }

    if (enrichedOriginal['endDate'] !== undefined) {
      enrichedOriginal['endDate'] = this.formatDateAsYYYY_MM_DD(
        enrichedOriginal['endDate']
      );
    }

    if (enrichedUpdated['startDate'] !== undefined) {
      enrichedUpdated['startDate'] = this.formatDateAsYYYY_MM_DD(
        enrichedUpdated['startDate']
      );
    }

    if (enrichedUpdated['endDate'] !== undefined) {
      enrichedUpdated['endDate'] = this.formatDateAsYYYY_MM_DD(
        enrichedUpdated['endDate']
      );
    }

    return {
      enrichedOriginal,
      enrichedUpdated,
    };
  }

  private formatDateAsYYYY_MM_DD(value: unknown): unknown {
    if (!value) {
      return value;
    }

    if (value instanceof Date) {
      const parsedDate = value;
      const year = parsedDate.getFullYear();
      const month = String(parsedDate.getMonth() + 1).padStart(2, '0');
      const day = String(parsedDate.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    }
    if (typeof value === 'string') {
      const parsedDate = new Date(value);
      if (Number.isNaN(parsedDate.getTime())) {
        return value;
      }
      const year = parsedDate.getFullYear();
      const month = String(parsedDate.getMonth() + 1).padStart(2, '0');
      const day = String(parsedDate.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    }
    return value;
  }

  private handleError(error: ErrorResponse): void {
    this.errorMessage = error?.errors[0].description;
    this.cdr.detectChanges();
    this.commonService.notify('error', this.errorMessage);
    this.loading = false;
  }

  showDeleteModal(index: number): void {
    this.isVisible = true;
    this.deleteId = index;
  }
  handleOkMiddle(): void {
    this.deleteSubscription(this.deleteId);
  }
  handleCancelMiddle(): void {
    this.isVisible = false;
    this.deleteId = 0;
  }

  deleteSubscription(index: number) {
    const subscriptions = this.subscriptionForm.get(
      'subscriptions'
    ) as FormArray;
    const row = subscriptions.at(index); // Get the FormGroup at the given index
    const data = row.value;
    if (data.id) {
      this.isDeleting = true;
      this.subscriptionService
        .deleteSubscription(data.id)
        .pipe(
          finalize(() => (this.isDeleting = false)),
          takeUntil(this.destroy$)
        )
        .subscribe({
          next: () => {
            this.loadData();
            this.orgService.refreshOrganizations();
            this.cdr.detectChanges();
            this.commonService.notify(
              'success',
              'Subscription deleted successfully'
            );
            this.isVisible = false;
            this.isDeleting = false;
            this.orgParams.mode = OrgManagementMode.VIEW;
          },
          error: error => {
            this.commonService.notify('error', error.message);
          },
        });
    } else {
      this.isDeleting = false;
      this.isVisible = false;
      this.removeRow(this.deleteId);
    }
    this.deleteId = 0;
    this.toggleDisable();
  }
  ngOnDestroy(): void {
    this.orgParams.mode = OrgManagementMode.VIEW;
    this.destroy$.next();
    this.destroy$.complete();
  }

  toggleDisable() {
    const subScriptionArray = this.subscriptionForm.get(
      'subscriptions'
    ) as FormArray;

    subScriptionArray.controls.forEach((control, index) => {
      if (this.editingIndex === index) {
        control.get('remarks')?.enable();
      } else {
        control.get('remarks')?.disable();
      }
    });
  }
}
